import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ValidatorFn } from '@angular/forms';
import { DeliveryDestinationNote } from 'customers';
import { NewCustomerAddress, NewCustomerCompany } from '../interfaces/new-customer.model';
import {
  NewCustomerDelivery,
  NewCustomerSearchFilter,
  NewCustomerSearchRequest,
  NewCustomerSearchType,
} from '../interfaces/new-customer-requests.model';

export const EMPTY_DELIVERY_NOTE: DeliveryDestinationNote = {
  inBolla: false,
  notaLibera: null,
  orari: null,
  contatto: null,
  giorni: null,
  sponda: false,
};

export function deliveryFromAddress(address: NewCustomerAddress): NewCustomerDelivery {
  return {
    branchId: address.branchId,
    tipoSede: address.tipoSede,
    riferimento: null,
    indirizzo: address.indirizzo ?? '',
    cap: address.cap ?? '',
    citta: address.comune ?? '',
    provincia: address.provincia ?? '',
    latitudine: address.latitudine ?? null,
    longitudine: address.longitudine ?? null,
    nota: { ...EMPTY_DELIVERY_NOTE },
  };
}

// Come i formatter del Flutter: maiuscole per codice fiscale e provincia, solo cifre per partita IVA, CAP e telefoni
export function upperCaseControl(control: FormControl<string>, destroyRef: DestroyRef): void {
  control.valueChanges.pipe(takeUntilDestroyed(destroyRef)).subscribe(value => {
    const upper = value.toUpperCase();
    if (upper !== value) {
      control.setValue(upper, { emitEvent: false });
    }
  });
}

export function digitsOnlyControl(control: FormControl<string>, destroyRef: DestroyRef): void {
  control.valueChanges.pipe(takeUntilDestroyed(destroyRef)).subscribe(value => {
    const digits = value.replace(/\D/g, '');
    if (digits !== value) {
      control.setValue(digits, { emitEvent: false });
    }
  });
}

// 16 caratteri alfanumerici oppure 11 numerici
export function isValidTaxCode(value: string): boolean {
  return /^([A-Z0-9]{16}|[0-9]{11})$/.test(value.toUpperCase());
}

export function isValidVat(value: string): boolean {
  return /^[0-9]{11}$/.test(value);
}

// 7 caratteri fra lettere maiuscole e numeri: non c'è un archivio su cui verificarlo
export function isValidSdi(value: string): boolean {
  return /^[A-Z0-9]{7}$/.test(value);
}

export function isValidEmail(value: string): boolean {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value);
}

// L'errore compare solo da 11 caratteri, quando il codice potrebbe essere completo
export const taxCodeFormatValidator: ValidatorFn = control => {
  const value = String(control.value ?? '');
  return value.length < 11 || isValidTaxCode(value) ? null : { taxCode: true };
};

export const sdiFormatValidator: ValidatorFn = control => {
  const value = String(control.value ?? '');
  return !value || isValidSdi(value) ? null : { sdi: true };
};

export const emailFormatValidator: ValidatorFn = control => {
  const value = String(control.value ?? '').trim();
  return !value || isValidEmail(value) ? null : { email: true };
};

export function searchType(request: NewCustomerSearchRequest): NewCustomerSearchType {
  return request.partitaIva ? 'partitaIva' : request.codiceFiscale ? 'codiceFiscale' : 'ragioneSociale';
}

export function searchValue(request: NewCustomerSearchRequest): string {
  return request.partitaIva ?? request.codiceFiscale ?? request.ragioneSociale ?? '';
}

export function searchTypeLabel(type: NewCustomerSearchType): string {
  switch (type) {
    case 'partitaIva':
      return 'Partita IVA';
    case 'codiceFiscale':
      return 'Codice fiscale';
    default:
      return 'Ragione sociale';
  }
}

// Il testo vale sul criterio della ricerca, comune e provincia sui valori delle aziende trovate
export function matchesSearchFilter(company: NewCustomerCompany, type: NewCustomerSearchType, filter: NewCustomerSearchFilter): boolean {
  const value = (company[type] ?? '').toLowerCase();
  const text = filter.text.trim().toLowerCase();

  return (!text || value.includes(text))
    && (!filter.comune || company.comune === filter.comune)
    && (!filter.provincia || company.provincia === filter.provincia);
}

// Cap e comune dell'indirizzo in una riga: «20100 - MILANO (MI)»
export function addressLocation(cap: string | null, comune: string | null, provincia: string | null): string {
  return `${cap ?? ''} - ${comune ?? ''} (${provincia ?? ''})`;
}
